import { Link, useLocation, useNavigate } from 'react-router-dom'
import type { ColumnDef } from '@tanstack/react-table'
import { ThumbsDown, ThumbsUp } from 'lucide-react'

import styles from './PrereadFeedback.module.scss'

import type { PrereadReport } from '@/api/prereadFeedback'
import DataTable from '@/components/DataTable'
import { formatUtcDateTime } from '@/utils/dates'

const ReportsTable = ({ reports, isLoading }: { reports: PrereadReport[]; isLoading: boolean }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const listReturn = { search: location.search }
  const columns: ColumnDef<PrereadReport>[] = [
    {
      header: 'Rating',
      enableSorting: false,
      cell: ({ row: { original: report } }) => (
        <Link
          to={`/app/preread-feedback/${report.id}`}
          state={listReturn}
          className={styles.ratingLink}
          aria-label={report.rating === 'up' ? 'Thumbs up' : 'Thumbs down'}
          onClick={(event) => event.stopPropagation()}
        >
          {report.rating === 'up' ? (
            <ThumbsUp size={16} aria-hidden="true" />
          ) : (
            <ThumbsDown size={16} aria-hidden="true" />
          )}
        </Link>
      ),
    },
    {
      header: 'Reported',
      enableSorting: false,
      cell: ({ row }) => (
        <time dateTime={row.original.reportedAt} className={styles.timestamp}>
          {formatUtcDateTime(row.original.reportedAt)}
        </time>
      ),
    },
    {
      header: 'Reporter',
      enableSorting: false,
      cell: ({ row }) => (
        <>
          {row.original.reporterEmail ?? 'Unknown'}
          <small className={styles.cellNote}>{row.original.source === 'internal' ? 'Internal' : 'External'}</small>
        </>
      ),
    },
    {
      header: 'Model',
      enableSorting: false,
      cell: ({ row }) => <span className={styles.model}>{row.original.model || '-'}</span>,
    },
    {
      header: 'Feedback',
      enableSorting: false,
      cell: ({ row }) => (
        <span className={styles.preview}>
          {row.original.feedbackPreview?.trim() ? row.original.feedbackPreview : '-'}
        </span>
      ),
    },
  ]

  return (
    <DataTable
      data={reports}
      columns={columns}
      onRowClick={(report, event) => {
        if ((event.target as Element).closest('a, button') || window.getSelection()?.toString()) return
        const detailPath = `/app/preread-feedback/${report.id}`
        if (event.metaKey || event.ctrlKey) window.open(detailPath, '_blank', 'noopener')
        else navigate(detailPath, { state: listReturn })
      }}
      manualPagination
      manualSorting
      isLoading={isLoading}
      ariaLabel="Preread feedback reports"
      loadingMessage="Loading preread reports…"
      emptyMessage="No preread feedback in this date window."
    />
  )
}

export default ReportsTable
